<template>
  <ModalWindow
      propId="modalInitialConditions"
      :prop-status="propStatus"
      :prop-static="true"
      :prop-is-open="propStatus"
      :prop-disable-close-button-footer="true"
      :prop-disable-close-button-header="true"
      @on-close-modal-click="onCloseModalClick">
    <template v-slot:header>
      <!-- Modal menu -->
      <div class="dropdown me-3">
        <a class="nav-link dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
          <i class="bi bi-menu-button-wide-fill pe-2"></i>
          <span>{{ $t('navbar.actions') }}</span>
        </a>
        <ul class="dropdown-menu">
          <li><h6 class="dropdown-header">{{ $t("navbar.dss") }}</h6></li>
          <li>
            <button class="dropdown-item" type="button" @click="onClickOpenModalInfo">
              <i class="bi bi-info-circle pe-2"></i>
              <span>{{ $t('buttons.AboutVisualization') }}</span>
            </button>
          </li>
          <template v-if="!propShowUpdate">
            <li>
              <hr class="dropdown-divider">
            </li>
            <li><h6 class="dropdown-header">{{ $t('navbar.goto') }}</h6></li>
            <li>
              <button class="dropdown-item" type="button" @click="onClickBackToHome">
                <i class="bi bi-house pe-2"></i>
                <span>{{ $t("navbar.home") }}</span>
              </button>
            </li>
            <li>
              <button class="dropdown-item" type="button" @click="onClickBackToVisualization">
                <i class="bi bi-binoculars pe-2"></i>
                <span>{{ $t("navbar.visualization") }}</span>
              </button>
            </li>
          </template>
          <li><h6 class="dropdown-header">{{ $t("navbar.support") }}</h6></li>
          <li>
            <button class="dropdown-item" type="button" @click="onClickOpenFeedback">
              <i class="bi bi-chat-quote-fill pe-2"></i>
              <span>{{ $t('navbar.feedback') }}</span>
            </button>
          </li>
        </ul>
      </div>
      <!-- Language change -->
      <div class="dropdown" v-if="propShowUpdate === false">
        <LangDropdown
            prop-id="modalInformationLangDropdown"
            :prop-locale="$i18n.locale"
        />
      </div>
    </template>
    <template v-slot:next_to_title></template>
    <template v-slot:close_modal>
      <button
          type="button"
          class="btn-close"
          aria-label="Close"
          v-if="propShowUpdate"
          @click="onCloseModalClick"
      >
      </button>
    </template>
    <div class="modal-body text-center pt-0">
      <h5 class="p-0 m-0">{{ $t('navbar.dss') }}</h5>
      <p><small>{{ $t('SimulationInputs.InputData') }}</small></p>

      <!-- Wizard Steps -->
      <div class="d-flex justify-content-between align-items-center pb-2 position-relative">
        <div v-for="(step, index) in steps.items" class="text-center step-container">
          <div v-if="index > 0"
               class="step-connector"
               :class="{
                'connector-active': steps.currentIndex >= index,
                'connector-completed': stepIsValid(step.id)
              }">
          </div>
          <div class="step-circle"
               :class="{
                  'step-active': stepCurrent.id === step.id,
                  'step_cursor_point': (stepCurrent.id !== step.id && (stepIsValid(step.id) || stepIsValid(stepPreviousIdIndex[step.id]) || stepPreviousIdIndex[step.id] === stepCurrent.id)),
                  'step-completed': (stepCurrent.id !== step.id && stepIsValid(step.id))
               }"
               @click="onClickGoToStep(step.id)">
            <i class="bi" :class="{
                'bi-calendar': step.id === constants.STEPS.MONTH,
                'bi-cloud-drizzle': step.id === constants.STEPS.PRECIPITATION_TEMP,
                'bi-snow': step.id === constants.STEPS.SNOW,
                'bi-droplet': step.id === constants.STEPS.SOIL_WATER
              }"></i>
          </div>
        </div>
      </div>
      <!-- Step Labels -->
      <div class="d-flex justify-content-between mb-4">
        <div v-for="(step, index) in steps.items" :key="'label' + index" class="text-center step-label-container">
          <div class="step-label text-break" style="display: block"
               :class="{
                  'step-label-active': stepCurrent.id === step.id,
                  'step-label-complete': (stepCurrent.id !== step.id && stepIsValid(step.id))
               }">{{ $t(step.label) }}
          </div>
        </div>
      </div>

      <!-- *** Step: Time Selection *** -->
      <div v-show="stepCurrent.id === constants.STEPS.MONTH" class="text-center">
        <p class="p-0 m-0 mb-3 form-text" v-html="$t('SimulationInformation.month')"></p>
        <div class="row mb-2">
          <div class="col-12">
            <MonthPicker @on-click-select-month="onClickSelectMonth" v-model="monthYear"/>
          </div>
        </div>
      </div>

      <!-- *** Step: Precipitation and Temperature Quantiles *** -->
      <div v-show="stepCurrent.id === constants.STEPS.PRECIPITATION_TEMP" class="text-center">
        <div class="row">
          <p class="p-0 m-0 mb-3 form-text" v-html="$t('SimulationInformation.precipitationTemperature')"></p>
          <!-- Precipitation / Temperature Selection -->
          <div class="col-12">
            <DropdownSingle
                :prop-model="precipitationTemperatureValue"
                :prop-option-list="precipitationTemperatureOptions"
                @on-select-value="onSelectPrecipitationTemperature"/>
          </div>
        </div>
      </div>

      <!-- *** Step: Snow Conditions *** -->
      <div v-show="stepCurrent.id === constants.STEPS.SNOW" class="text-center">
        <div class="text-dark form-text mb-4 py-3 border border-purple border-2 rounded-1 shadow"
             v-html="$t('alerts.snowConditionsDisabled')"
             v-show="stepMonthIsSummer">
        </div>
        <p class="p-0 m-0 mb-3 form-text" v-html="$t('SimulationInformation.snowCondition')"></p>
        <div class="row">
          <div class="col-12 col-sm-4 mb-1 mb-sm-0">
            <input type="radio" id="snow_quantile_20"
                   v-model="snowStateQuantile"
                   :value="constants.PERCENTILES.BELOW_MEAN"
                   class="btn-check"
                   :disabled="stepMonthIsSummer === true"
                   autocomplete="off"
                   @change="focusNextStepButton"/>
            <label for="snow_quantile_20" class="btn btn-outline-primary w-100">
              {{ $t('InputSearchFormLabelValue.PercentileBelowAvgShort') }}
            </label>
          </div>
          <div class="col-12 col-sm-4 mb-1 mb-sm-0">
            <input type="radio" id="snow_quantile_50" v-model="snowStateQuantile"
                   :value="constants.PERCENTILES.MEAN"
                   class="btn-check"
                   :disabled="stepMonthIsSummer === true"
                   autocomplete="off" @change="focusNextStepButton"/>
            <label for="snow_quantile_50" class="btn btn-outline-primary w-100">
              {{ $t('InputSearchFormLabelValue.PercentileOnAverageShort') }}
            </label>
          </div>
          <div class="col-12 col-sm-4">
            <input type="radio" id="snow_quantile_70" v-model="snowStateQuantile"
                   :value="constants.PERCENTILES.ABOVE_MEAN"
                   class="btn-check"
                   :disabled="stepMonthIsSummer === true"
                   autocomplete="off" @change="focusNextStepButton"/>
            <label for="snow_quantile_70" class="btn btn-outline-primary w-100">
              {{ $t('InputSearchFormLabelValue.PercentileAboveAvgShort') }}
            </label>
          </div>
        </div>
      </div>

      <!-- *** Step: Soil Water Content *** -->
      <div v-show="stepCurrent.id === constants.STEPS.SOIL_WATER" class="text-center">
        <p class="p-0 m-0 mb-3 form-text" v-html="$t('SimulationInformation.soilWater')"></p>
        <div class="row">
          <div class="col-12 col-sm-4 mb-1 mb-sm-0">
            <input type="radio" id="soil_water_content_20" v-model="soilWaterQuantile"
                   :value="constants.PERCENTILES.BELOW_MEAN"
                   class="btn-check" autocomplete="off" @change="focusNextStepButton"/>
            <label for="soil_water_content_20" class="btn btn-outline-primary w-100">
              {{ $t('InputSearchFormLabelValue.PercentileBelowAvgShort') }}
            </label>
          </div>
          <div class="col-12 col-sm-4 mb-1 mb-sm-0">
            <input type="radio" id="soil_water_content_50" v-model="soilWaterQuantile"
                   :value="constants.PERCENTILES.MEAN"
                   class="btn-check" autocomplete="off" @change="focusNextStepButton"/>
            <label for="soil_water_content_50" class="btn btn-outline-primary w-100">
              {{ $t('InputSearchFormLabelValue.PercentileOnAverageShort') }}
            </label>
          </div>
          <div class="col-12 col-sm-4">
            <input type="radio" id="soil_water_content_70" v-model="soilWaterQuantile"
                   :value="constants.PERCENTILES.ABOVE_MEAN"
                   class="btn-check" autocomplete="off" @change="focusNextStepButton"/>
            <label for="soil_water_content_70" class="btn btn-outline-primary w-100">
              {{ $t('InputSearchFormLabelValue.PercentileAboveAvgShort') }}
            </label>
          </div>
        </div>
      </div>
      <!-- Error message -->
      <div class="text-danger form-text mt-2" v-show="showError">
        {{ $t("alerts.selectValueBefore") }}
      </div>
    </div>

    <template #footer>
      <div class="d-flex w-100 justify-content-between">
        <div>
          <button type="button" class="btn btn-outline-secondary me-2"
                  :class="{'previous_button_dss': steps.currentIndex >= 1}"
                  :disabled="steps.currentIndex === 0"
                  @click="onClickGotoPreviousStep">
            <i class="bi bi-caret-left me-2"></i>
            <span>{{ $t("Actions.Previous") }}</span>
          </button>
          <button type="button" class="btn next_button_dss focus-ring focus-ring-dark"
                  @click="onClickGotoNextStep"
                  ref="btnNextStep"
                  :disabled="!stepCurrentIsValid || steps.currentIndex === steps.items.length - 1">
            <span>{{ $t("Actions.Next") }}</span>
            <i class="bi bi-caret-right ps-2"></i>
          </button>
        </div>
        <div>
          <button type="button" class="btn btn-success text-white focus-ring focus-ring-dark"
                  ref="btnSave"
                  @click="saveAndClose"
                  :disabled="stepsAllValid === false">
            <template v-if="propShowUpdate === false">
              <i class="bi bi-save me-2"></i>
              <span>{{ $t("Actions.Save") }}</span>
            </template>
            <template v-else>
              <i class="bi bi-pencil-square me-2"></i>
              <span>{{ $t("Actions.Update") }}</span>
            </template>
          </button>
        </div>
      </div>
    </template>
  </ModalWindow>

  <!-- Toasts -->
  <Toast prop-id="toastInitialConditionsSuccess"
         prop-type="success"
         :prop-trigger="toastSuccess"
         @on-toast-hidden="onToastHidden">
    <template v-slot:body>
      <span v-html="$t('alerts.initialConditionsUpdatedSuccess')"></span>
    </template>
  </Toast>
</template>

<script>
import constants from "@/libs/Constants";
import VueDatePicker from '@vuepic/vue-datepicker';
import ModalWindow from '@/components/ModalWindow.vue';
import MonthPicker from "@/components/MonthPicker.vue";
import LangDropdown from "@/components/LangDropdown.vue";
import DropdownSingleOption from "@/components/DropdownSingleOption.vue";
import {SimulationData} from '@/libs/SimulationData';
import Toast from "@/components/Toast.vue";

export default {
  name: 'ModalInitialConditions',
  components: {
    Toast,
    DropdownSingle: DropdownSingleOption,
    MonthPicker,
    ModalWindow,
    LangDropdown,
    VueDatePicker
  },
  props: {
    propStatus: Boolean,
    propShowUpdate: Boolean,
    propInitialStep: String,
    propInitialValues: SimulationData
  },
  emits: [
    'onClickOpenInfo',
    'onCloseModalClick',
    'onClickOpenFeedback',
    'onClickSaveData'
  ],
  data() {
    let steps = [
      {id: constants.STEPS.MONTH, label: "SimulationInputs.Month"},
      {id: constants.STEPS.SNOW, label: "SimulationInputs.Snow"},
      {id: constants.STEPS.SOIL_WATER, label: "SimulationInputs.SoilWater"},
      {id: constants.STEPS.PRECIPITATION_TEMP, label: "SimulationInputs.PrecipitationTemperature"}
    ];

    return {
      constants,

      steps: {
        items: steps,
        completed: [],
        currentIndex: 0
      },

      //Inputs
      monthYear: null,
      precipitationTemperatureParent: null,
      precipitationTemperatureValue: null,
      precipitationQuantile: null,
      temperatureQuantile: null,

      snowStateQuantile: null,
      soilWaterQuantile: null,

      showError: false,
      toastSuccess: false,
    };
  },
  watch: {
    monthYear(newValue, oldValue) {
      if (this.stepMonthIsSummer === true && this.snowStateQuantile === null)
        this.snowStateQuantile = constants.PERCENTILES.MEAN
    },
    temperatureQuantile(newValue, oldValue) {
      if (newValue !== null) {
        this.showError = false;
        this.precipitationQuantile = null;
      }
    },
    precipitationQuantile(newValue, oldValue) {
      if (newValue !== null) {
        this.showError = false;
        this.temperatureQuantile = null;
      }
    },
    propInitialValues(newValue, oldValue) {
      if (newValue !== null) {
        this.monthYear = newValue.month;

        this.precipitationTemperatureValue = newValue.precipitationTemperatureValue;
        this.precipitationQuantile = newValue.precipitationQuantile;
        this.temperatureQuantile = newValue.temperatureQuantile;

        this.snowStateQuantile = newValue.snowStateQuantile;
        this.soilWaterQuantile = newValue.soilWaterQuantile;
      }
    },
    propInitialStep(newValue, oldValue) {
      if (newValue !== null)
        this.steps.currentIndex = this.stepIdIndex[newValue];
    }
  },
  methods: {
    onClickSelectMonth(month) {
      this.focusNextStepButton();
    },
    onSelectPrecipitationTemperature(value, parent) {
      //Get mappings for each child
      let mappings = {}
      this.precipitationTemperatureOptions.forEach(parent => {
        parent.children.forEach(child => {
          mappings[child.value] = child.mapping
        });
      });

      this.precipitationTemperatureValue = value;

      let mappedValue = mappings[value];

      if (mappedValue !== null && mappedValue !== undefined) {
        switch (parent) {
          case constants.PRECIPITATION_TEMPERATURE.PRECIPITATION:
            this.precipitationQuantile = mappedValue;
            break;
          case constants.PRECIPITATION_TEMPERATURE.TEMPERATURE:
            this.temperatureQuantile = mappedValue;
            break;
          default:
            this.precipitationQuantile = null;
            this.temperatureQuantile = null;
            this.precipitationTemperatureParent = null;
        }

        this.focusNextStepButton();
      }
    },
    focusNextStepButton() {
      this.showError = false;

      setTimeout(() => {
        if (this.steps.currentIndex !== this.steps.items.length - 1) {
          this.$refs.btnNextStep.focus();
        } else {
          this.$refs.btnSave.focus();
        }
      }, 0);
    },
    stepIsValid(stepId) {
      switch (stepId) {
        case constants.STEPS.MONTH:
          return !!this.monthYear;
        case constants.STEPS.PRECIPITATION_TEMP:
          return this.precipitationQuantile !== null || this.temperatureQuantile !== null;
        case constants.STEPS.SNOW:
          return this.snowStateQuantile !== null;
        case constants.STEPS.SOIL_WATER:
          return this.soilWaterQuantile !== null;
        default:
          return false;
      }
    },
    onClickGotoNextStep() {
      this.onClickGoToStep(this.stepNextIdIndex[this.stepCurrent.id]);
    },
    onClickGotoPreviousStep() {
      this.onClickGoToStep(this.stepPreviousIdIndex[this.stepCurrent.id])
    },
    onClickGoToStep(stepId) {
      if (this.stepPreviousIdIndex[this.stepCurrent.id] !== stepId && this.stepCurrentIsValid === false)
        this.showError = true;
      else {
        this.showError = false;
        this.steps.currentIndex = this.stepIdIndex[stepId];
      }
    },
    saveAndClose() {
      if (this.stepsAllValid === true) {
        const data = new SimulationData(
            this.monthYear,
            this.precipitationTemperatureValue,
            this.precipitationQuantile,
            this.temperatureQuantile,
            this.snowStateQuantile,
            this.soilWaterQuantile,
        );
        this.$emit('onClickSaveData', data);

        if (this.propShowUpdate === true)
          this.toastSuccess = true;

      } else {
        this.showError = true;
      }
    },
    onToastHidden() {
      this.toastSuccess = false;
    },
    onClickOpenFeedback() {
      this.$emit('onClickOpenFeedback');
    },
    onClickOpenModalInfo() {
      this.$emit('onClickOpenInfo')
    },
    onClickBackToHome() {
      this.onCloseModalClick();
      this.$router.push({name: constants.ROUTING.LANDING, replace: true});
    },
    onClickBackToVisualization() {
      this.onCloseModalClick();
      this.$router.push({name: constants.ROUTING.VISUALIZATION, replace: true});
    },
    onCloseModalClick() {
      this.$emit('onCloseModalClick')
    }
  },
  computed: {
    precipitationTemperatureOptions() {
      return [
        {
          id: constants.PRECIPITATION_TEMPERATURE.PRECIPITATION,
          name: this.$t('SimulationInputs.Precipitation'),
          bootstrapIcon: 'cloud-drizzle',
          children: [
            {
              name: this.$t('InputSearchFormLabelValue.PercentileBelowAvgShort'),
              value: constants.PRECIPITATION_TEMPERATURE.OPTIONS.PRECIPITATION_BELOW_MEAN,
              mapping: constants.PERCENTILES.BELOW_MEAN
            },
            {
              name: this.$t('InputSearchFormLabelValue.PercentileOnAverageShort'),
              value: constants.PRECIPITATION_TEMPERATURE.OPTIONS.PRECIPITATION_MEAN,
              mapping: constants.PERCENTILES.MEAN
            },
            {
              name: this.$t('InputSearchFormLabelValue.PercentileAboveAvgShort'),
              value: constants.PRECIPITATION_TEMPERATURE.OPTIONS.PRECIPITATION_ABOVE_MEAN,
              mapping: constants.PERCENTILES.ABOVE_MEAN
            }
          ]
        },
        {
          id: constants.PRECIPITATION_TEMPERATURE.TEMPERATURE,
          name: this.$t('SimulationInputs.Temperature'),
          bootstrapIcon: 'thermometer-sun',
          children: [
            {
              name: this.$t('InputSearchFormLabelValue.PercentileBelowAvgShort'),
              value: constants.PRECIPITATION_TEMPERATURE.OPTIONS.TEMPERATURE_BELOW_MEAN,
              mapping: constants.PERCENTILES.BELOW_MEAN
            },
            {
              name: this.$t('InputSearchFormLabelValue.PercentileOnAverageShort'),
              value: constants.PRECIPITATION_TEMPERATURE.OPTIONS.TEMPERATURE_MEAN,
              mapping: constants.PERCENTILES.MEAN
            },
            {
              name: this.$t('InputSearchFormLabelValue.PercentileAboveAvgShort'),
              value: constants.PRECIPITATION_TEMPERATURE.OPTIONS.TEMPERATURE_ABOVE_MEAN,
              mapping: constants.PERCENTILES.ABOVE_MEAN
            }
          ]
        }
      ];
    },
    stepCurrent() {
      return this.steps.items[this.steps.currentIndex];
    },
    stepIdIndex() {
      let stepIndex = {};
      this.steps.items.forEach((item, index) => {
        stepIndex[item.id] = index;
      })
      return stepIndex;
    },
    stepPreviousIdIndex() {
      let previousIndex = {};

      let previous = null;
      this.steps.items.forEach(item => {
        previousIndex[item.id] = previous;
        previous = item.id;
      });
      return previousIndex;
    },
    stepNextIdIndex() {
      let nextIndex = {};

      let next = null;
      this.steps.items.toReversed().forEach(item => {
        nextIndex[item.id] = next;
        next = item.id;
      });
      return nextIndex;
    },
    stepCurrentIsValid() {
      return this.stepIsValid(this.stepCurrent.id);
    },
    stepsAllValid() {
      return this.steps.items.every(step => {
        return this.stepIsValid(step.id) === true;
      });
    },
    stepMonthIsSummer() {
      return this.monthYear >= 6 && this.monthYear <= 9;
    }
  }
};
</script>